import type { UserRole } from '@/types/auth'

export const PERMISSIONS = {
  company: ['read', 'create', 'update', 'deactivate'],
  driver: ['read', 'create', 'update', 'deactivate'],
  vehicle: ['read', 'create', 'update', 'deactivate', 'view_financial_summary'],
  freight: ['read', 'create', 'update', 'assign', 'set_value', 'complete'],
  invoice: ['read', 'create', 'update', 'pay', 'delete'],
  repayment: ['read', 'update', 'pay'],
  cash: ['read', 'write', 'reverse', 'manage_accounts', 'manage_categories', 'manage_opening_balance'],
  obligation: ['read', 'write', 'settle'],
  expense: ['read', 'create'],
  employee: ['read', 'write', 'pay_salary'],
  reports: ['view'],
  team: ['read', 'invite', 'manage_users', 'manage_roles'],
  settings: ['appearance', 'view_audit'],
} as const

type Module = keyof typeof PERMISSIONS
type ActionOf<M extends Module> = (typeof PERMISSIONS)[M][number]

export type Permission = {
  [M in Module]: `${M}.${ActionOf<M>}`
}[Module]

export const ALL_PERMISSIONS: readonly Permission[] = Object.entries(PERMISSIONS).flatMap(
  ([mod, actions]) => actions.map((a) => `${mod}.${a}` as Permission),
)

const FINANCIAL_PERMISSIONS: Permission[] = [
  'company.read',
  'driver.read',
  'vehicle.read',
  'vehicle.view_financial_summary',
  'freight.read',
  'freight.update',
  'freight.set_value',
  'invoice.read',
  'repayment.read',
  'repayment.update',
  'cash.read',
  'cash.write',
  'cash.reverse',
  'cash.manage_accounts',
  'cash.manage_categories',
  'obligation.read',
  'obligation.write',
  'obligation.settle',
  'expense.read',
  'expense.create',
  'employee.read',
  'employee.write',
  'employee.pay_salary',
  'reports.view',
]

const OPERATOR_PERMISSIONS: Permission[] = [
  'company.read',
  'company.create',
  'company.update',
  'company.deactivate',
  'driver.read',
  'driver.create',
  'driver.update',
  'driver.deactivate',
  'vehicle.read',
  'vehicle.create',
  'vehicle.update',
  'vehicle.deactivate',
  'freight.read',
  'freight.create',
  'freight.update',
  'freight.assign',
  'freight.complete',
  'invoice.read',
  'invoice.create',
  'invoice.update',
  'invoice.pay',
  'repayment.read',
  'repayment.pay',
  'cash.read',
  'expense.read',
]

export const ROLE_PRESETS: Record<UserRole, readonly Permission[]> = {
  ADMIN: ALL_PERMISSIONS,
  FINANCIAL: FINANCIAL_PERMISSIONS,
  OPERATOR: OPERATOR_PERMISSIONS,
}

export function permissionsFor(role: UserRole): readonly Permission[] {
  return ROLE_PRESETS[role]
}

export function hasPermission(role: UserRole, permission: Permission): boolean {
  return ROLE_PRESETS[role].includes(permission)
}
